import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { SniperGroup } from '../../types/sniper.types';
import {
  deriveSniperGroupBadge,
  sniperGroupMarketCount,
} from '../../utils/sniperGroupState';
import { SniperGroupControls } from './SniperGroupControls';

/**
 * The Sniper groups landing — the first thing on `/predictions/sniper`. A LIST
 * of groups; each card shows the name, market count, a live state badge
 * (running+mode / stopped / panicked) and the per-group Start(test|normal) /
 * Stop / Panic controls. Clicking the card body opens the group's DETAIL.
 * Mirrors the opener `GroupPickerLanding` (a picker list, controls inline).
 */
export function SniperGroupsLanding({
  groups,
  onSelectGroup,
  onDeleteGroup,
  deleteBusy,
}: {
  groups: SniperGroup[];
  onSelectGroup: (groupId: string) => void;
  onDeleteGroup: (group: SniperGroup) => void;
  deleteBusy?: boolean;
}) {
  return (
    <Stack spacing={1}>
      {groups.map((g) => {
        const badge = deriveSniperGroupBadge(g);
        const markets = sniperGroupMarketCount(g);
        return (
          <Paper
            key={g._id}
            variant="outlined"
            sx={{
              p: 1.5,
              borderColor: badge.panicked
                ? 'error.main'
                : g.running
                  ? 'success.main'
                  : 'divider',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ md: 'center' }}
            >
              <Box
                role="button"
                tabIndex={0}
                onClick={() => onSelectGroup(g._id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onSelectGroup(g._id);
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'common.white', fontWeight: 600 }}
                >
                  {g.name}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${g.events?.length ?? 0} event(s) · ${markets} market(s)`}
                />
                <Chip
                  size="small"
                  color={badge.severity}
                  variant={badge.severity === 'default' ? 'outlined' : 'filled'}
                  label={badge.label}
                  sx={{ fontWeight: 700 }}
                />
                <ChevronRightIcon sx={{ color: 'grey.600' }} />
              </Box>

              <SniperGroupControls group={g} />

              <Tooltip title="Delete group (cascades markets)">
                <span>
                  <IconButton
                    onClick={() => onDeleteGroup(g)}
                    disabled={deleteBusy}
                    aria-label="delete group"
                    size="small"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}
